import { useEffect } from 'react'

import {
  ConnectionState,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
  TrackEvent,
} from 'livekit-client'

import {
  dimensionsForDowngradedRequest,
  layerHeightsFromTrackLayers,
  resolveDowngradedCameraQuality,
} from './mobileRemoteCameraQuality'

/**
 * На телефоне опускает чужие камеры на слой ниже, чем просит размер плитки.
 *
 * Свою камеру и демонстрацию экрана не трогает. Если высоту плитки ещё нельзя
 * измерить, ставим средний слой: это безопаснее для батареи, чем верхний.
 *
 * @param room
 * @param enabled - телефон вне комнаты повышенного качества
 */
export function useMobileRemoteCameraQualityDowngrade(room: Room | undefined, enabled: boolean) {
  useEffect(() => {
    if (!room || !enabled) {
      return
    }

    const detachBySid = new Map<string, () => void>()

    /**
     * Снимает слушатели с одной чужой камеры по её идентификатору.
     */
    const detachPublication = (trackSid: string) => {
      const detach = detachBySid.get(trackSid)

      if (!detach) {
        return
      }

      detach()
      detachBySid.delete(trackSid)
    }

    /**
     * Просит сервер отдать слой на ступень ниже размера плитки.
     *
     * Шлём ширину и высоту слоя, а не quality: иначе adaptiveStream подменит
     * запрос размером плитки, и сервер снова выберет слой без шага вниз.
     *
     * @param requested - размер плитки от adaptiveStream; пусто, пока элемент не прикреплён
     */
    const applyDowngrade = (
      publication: RemoteTrackPublication,
      requested: Track.Dimensions | undefined,
    ) => {
      const layers = publication.trackInfo?.layers
      const layerHeights = layerHeightsFromTrackLayers(layers)
      const nextQuality = resolveDowngradedCameraQuality(layerHeights, requested?.height)

      const cappedDimensions = dimensionsForDowngradedRequest(layers, nextQuality, requested)

      if (cappedDimensions) {
        publication.setVideoDimensions(cappedDimensions)

        return
      }

      publication.setVideoQuality(nextQuality)
    }

    /**
     * Считывает размер плитки так же, как adaptiveStream: CSS-размер и плотность экрана.
     */
    const readRequestedDimensions = (track: Track): Track.Dimensions | undefined => {
      const element = track.attachedElements[0]

      if (!element || element.clientWidth <= 0 || element.clientHeight <= 0) {
        return undefined
      }

      // ActiveRoom считает плотность по экрану, поэтому здесь тоже берём
      // devicePixelRatio: иначе ступень разойдётся с тем, что видит adaptiveStream.
      const pixelDensity =
        typeof window !== 'undefined' && window.devicePixelRatio > 0
          ? window.devicePixelRatio
          : 1

      return {
        width: element.clientWidth * pixelDensity,
        height: element.clientHeight * pixelDensity,
      }
    }

    /**
     * Подписывается на размер плитки чужой камеры и сразу считает ступень.
     */
    const attachPublication = (track: Track, publication: RemoteTrackPublication) => {
      if (publication.source !== Track.Source.Camera) {
        return
      }

      if (detachBySid.has(publication.trackSid)) {
        return
      }

      const handleDimensionsChanged = (dimensions: Track.Dimensions) => {
        applyDowngrade(publication, dimensions)
      }

      const handleElementAttached = () => {
        applyDowngrade(publication, readRequestedDimensions(track))
      }

      track.on(TrackEvent.VideoDimensionsChanged, handleDimensionsChanged)
      track.on(TrackEvent.ElementAttached, handleElementAttached)

      detachBySid.set(publication.trackSid, () => {
        track.off(TrackEvent.VideoDimensionsChanged, handleDimensionsChanged)
        track.off(TrackEvent.ElementAttached, handleElementAttached)
      })

      applyDowngrade(publication, readRequestedDimensions(track))
    }

    const handleTrackSubscribed = (track: RemoteTrack, publication: RemoteTrackPublication) => {
      attachPublication(track, publication)
    }

    const handleTrackUnsubscribed = (_track: RemoteTrack, publication: RemoteTrackPublication) => {
      detachPublication(publication.trackSid)
    }

    /**
     * Подхватывает камеры, на которые комната уже подписалась до монтирования хука.
     */
    const syncExistingSubscriptions = () => {
      room.remoteParticipants.forEach((participant) => {
        participant.getTrackPublications().forEach((publication) => {
          if (
            publication instanceof RemoteTrackPublication &&
            publication.source === Track.Source.Camera &&
            publication.track
          ) {
            attachPublication(publication.track, publication)
          }
        })
      })
    }

    room.on(RoomEvent.Connected, syncExistingSubscriptions)
    room.on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
    room.on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)

    if (room.state === ConnectionState.Connected) {
      syncExistingSubscriptions()
    }

    return () => {
      room.off(RoomEvent.Connected, syncExistingSubscriptions)
      room.off(RoomEvent.TrackSubscribed, handleTrackSubscribed)
      room.off(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)

      detachBySid.forEach((detach) => {
        detach()
      })
      detachBySid.clear()
    }
  }, [room, enabled])
}
